import {HoistModel} from '@xh/hoist/core';
import {genDisplayName} from '@xh/hoist/data';
import {action, observable, observableRef} from '@xh/hoist/mobx';
import {isEmpty, last} from 'lodash';
import {ApplySpecOpsResult, FieldCatalog, FieldCatalogEntry, RejectedSpecOp} from './SpecTypes';

export type SpecChangeSource = 'manual' | 'ai' | 'grid' | 'chart' | 'system';

export interface DispatchOptions {
    source: SpecChangeSource;
    /** Undo label - defaults to a description of the ops. */
    label?: string;
    /**
     * Consecutive dispatches with the same key are merged into a single undo entry - for typing in
     * a text input, or a column being resized.
     */
    coalesceKey?: string;
}

export interface SpecHistoryEntry<S> {
    spec: S;
    label: string;
    source: SpecChangeSource;
    coalesceKey?: string;
}

export interface SpecContext<C> {
    catalog: FieldCatalog;
    capabilities: C;
}

/** The pure functions that define one kind of spec - its reducer and helpers. */
export interface SpecReducer<S, O, C> {
    applyOps(spec: S, ops: O[], ctx: SpecContext<C>): ApplySpecOpsResult<S, O>;
    normalizeSpec(spec: Partial<S>, defaultSpec: S): S;
    describeOps(ops: O[]): string;
    /** The op that replaces the whole spec - used by reset and to load (and validate) a saved spec. */
    replaceOp(spec: S): O;
}

export interface SpecModelConfig<S, C> {
    catalog: FieldCatalog;
    capabilities: C;
    /** The spec used initially when there is no saved spec, and by "Reset". */
    defaultSpec: S;
    /** Previously saved spec, if any. */
    initialSpec?: S;
    /** Called (debounced) with the spec after every change. */
    persistCallback?: (spec: S) => void;
}

const MAX_HISTORY = 50;

/**
 * Owns the spec for one configurable component. `dispatch()` is the only way to change it - the
 * configurator, the AI assistant and the component's own interactions all go through it, so there
 * is a single ordered history of changes (with undo/redo) no matter where they came from.
 */
export class SpecModel<S, O, C> extends HoistModel {
    @observableRef accessor spec: S;
    /** Incremented on every change. */
    @observable accessor revision = 0;

    @observableRef accessor undoStack: SpecHistoryEntry<S>[] = [];
    @observableRef accessor redoStack: SpecHistoryEntry<S>[] = [];

    /** Ops rejected by the last dispatch. */
    @observableRef accessor lastRejected: RejectedSpecOp<O>[] = [];

    readonly catalog: FieldCatalog;
    readonly capabilities: C;
    readonly defaultSpec: S;
    readonly reducer: SpecReducer<S, O, C>;

    constructor(reducer: SpecReducer<S, O, C>, {catalog, capabilities, defaultSpec, initialSpec, persistCallback}: SpecModelConfig<S, C>) {
        super();

        this.reducer = reducer;
        this.catalog = catalog;
        this.capabilities = capabilities;
        this.defaultSpec = reducer.normalizeSpec(defaultSpec, defaultSpec);
        this.spec = this.loadSpec(initialSpec);

        if (persistCallback) {
            this.addReaction({
                track: () => this.spec,
                run: spec => persistCallback(spec),
                debounce: 300
            });
        }
    }

    get context(): SpecContext<C> {
        return {catalog: this.catalog, capabilities: this.capabilities};
    }

    get canUndo(): boolean {
        return !isEmpty(this.undoStack);
    }

    get canRedo(): boolean {
        return !isEmpty(this.redoStack);
    }

    get undoLabel(): string {
        return last(this.undoStack)?.label;
    }

    get redoLabel(): string {
        return last(this.redoStack)?.label;
    }

    getField(name: string): FieldCatalogEntry {
        return this.catalog.find(f => f.name === name) ?? null;
    }

    fieldLabel(name: string): string {
        return this.getField(name)?.label ?? genDisplayName(name);
    }

    describeOps(ops: O[]): string {
        return this.reducer.describeOps(ops);
    }

    /**
     * Apply ops as one undoable change. Invalid ops are skipped and reported in the result (and in
     * `lastRejected`); the valid ones still apply.
     */
    @action
    dispatch(ops: O[], {source, label, coalesceKey}: DispatchOptions): ApplySpecOpsResult<S, O> {
        this.beforeChange();
        const result = this.reducer.applyOps(this.spec, ops, this.context);
        this.lastRejected = result.rejected;
        if (isEmpty(result.applied)) return result;

        const prev = last(this.undoStack),
            coalesce = coalesceKey && prev?.coalesceKey === coalesceKey && isEmpty(this.redoStack);

        if (!coalesce) {
            const entry: SpecHistoryEntry<S> = {spec: this.spec, source, label: label ?? this.describeOps(result.applied), coalesceKey};
            this.undoStack = [...this.undoStack, entry].slice(-MAX_HISTORY);
        }
        this.redoStack = [];
        this.setSpec(result.spec);
        this.afterChange();
        return result;
    }

    @action
    undo() {
        const entry = last(this.undoStack);
        if (!entry) return;
        this.beforeChange();
        this.undoStack = this.undoStack.slice(0, -1);
        this.redoStack = [...this.redoStack, {...entry, spec: this.spec, coalesceKey: null}];
        this.setSpec(entry.spec);
        this.afterChange();
    }

    @action
    redo() {
        const entry = last(this.redoStack);
        if (!entry) return;
        this.beforeChange();
        this.redoStack = this.redoStack.slice(0, -1);
        this.undoStack = [...this.undoStack, {...entry, spec: this.spec, coalesceKey: null}];
        this.setSpec(entry.spec);
        this.afterChange();
    }

    reset() {
        this.dispatch([this.reducer.replaceOp(this.defaultSpec)], {source: 'manual', label: 'Reset to default'});
    }

    /**
     * Take a spec that changed outside `dispatch()`, without an undo entry - for a host whose state the
     * user also changes directly (a dashboard canvas), which reads it back in `beforeChange()`.
     */
    @action
    adoptSpec(spec: S) {
        this.setSpec(spec);
    }

    @action
    clearHistory() {
        this.undoStack = [];
        this.redoStack = [];
        this.lastRejected = [];
    }

    //------------------------
    // For subclasses whose spec mirrors state held elsewhere
    //------------------------
    /** Called before a dispatch, undo or redo changes the spec - to bring the spec up to date first. */
    protected beforeChange() {}

    /** Called after a dispatch, undo or redo changed the spec - to carry the new spec over. */
    protected afterChange() {}

    //------------------------
    // Implementation
    //------------------------
    @action
    private setSpec(spec: S) {
        this.spec = spec;
        this.revision++;
    }

    /** Load a saved spec, dropping anything no longer valid (e.g. a field removed from the catalog). */
    private loadSpec(saved: S): S {
        if (!saved) return this.defaultSpec;

        const {spec, rejected} = this.reducer.applyOps(this.defaultSpec, [this.reducer.replaceOp(saved)], this.context);
        if (!isEmpty(rejected)) {
            this.logWarn('Saved spec is invalid - using the default', rejected[0].reason);
        }
        return spec;
    }
}
